import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import CashFlow from "./CashFlow";
import ErrorMessage from "../../../components/ui/errorMessage";
import PortfolioTrackerHeader from "../header";
import { Filters } from "../Filters";
import { fetchTrackerIndex } from "../../../store/slice/myTrackerSlice";
import { MEMBER_KEY, MyTrackerTabs, USER_DETAIL } from "../../../utils/constants";
import KeyMemberAtGlance from "./KeyMemberAtGlance";
import Equity from "./Equity";
import { ICashflow } from "../../../interface/propertyTracker";
import MyPropertyAtGlance from "./MyPropertyAtGlance";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import { useFilterSelection } from "../FilterSelectionContext";
import { getCurrentFinancialYear } from "../../../utils/commonUtil";
import { REIProgressSpinner } from "../../../components/ui";

const MyTrackerIndex = () => {
  const error = useSelector((state: RootState) => state.myTracker.error);
  const isLoading = useSelector((state: RootState) => state.myTracker.loading);
  const { selectedProfiles, selectedEntityId, isShowingTotals, selectedYear, setSelectedYear, setSelectedEntityId } =
    useFilterSelection();
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const memberId = localStorage.getItem(MEMBER_KEY) || "";
  const dispatch: AppDispatch = useDispatch();
  const trackerData = useSelector((state: RootState) => state.myTracker.trackerData);
  const [dataExist, setDataExist] = useState<boolean>(false);
  const [filteredCashFlowData, setFilteredCashFlowData] = useState<{
    Actuals: ICashflow[];
    Forecast: ICashflow[];
    isFromMyTracker?: boolean;
  }>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let Entity;
    let ProfileData;
    setLoading(true);
    ProfileData =
      selectedProfiles.length > 0
        ? selectedProfiles
            .sort((a, b) => a.MemberProfileId - b.MemberProfileId)
            .map((profile) => ({ ProfileId: profile.MemberProfileId }))
        : [];
    Entity = selectedEntityId != 0 ? [{ EntityId: selectedEntityId }] : [];

    const parameter = {
      MemberId: memberId,
      UserCountryCode: memberDetail.Country,
      Entity: Entity,
      Profiles: ProfileData,
      MemberName: `${memberDetail.FirstName} ${memberDetail.LastName}`,
    };
    dispatch(fetchTrackerIndex(parameter));
  }, [dispatch, memberId, selectedEntityId, selectedProfiles, isShowingTotals]);

  useEffect(() => {
    if (!trackerData || trackerData.length === 0) {
      setDataExist(false);
      setLoading(false);
      return;
    }
    if (trackerData && trackerData.length != 0) {
      if (trackerData[0]?.Properties && trackerData[0]?.Properties.length > 0) {
        setDataExist(true);
      } else {
        setDataExist(false);
      }
      filterData();
    }
  }, [trackerData]);

  const filterData = () => {
    if (!trackerData) return;
    let totalData;
    const entityIdToCompare = selectedEntityId === 0 ? "" : selectedEntityId.toString();

    if (isShowingTotals) {
      totalData = trackerData.find((total) => total.EntityId === entityIdToCompare)?.CashflowTotals;
    } else {
      totalData = trackerData.find((total) => total.EntityId === entityIdToCompare)?.CashflowPortions;
    }
    if (totalData) {
      setFilteredCashFlowData({
        Actuals: totalData.Actuals,
        Forecast: totalData.Forecast,
        isFromMyTracker: true,
      });
    }
    setLoading(false);
  };

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <>
      {loading ||
        (isLoading && (
          <div className="text-center">
            <REIProgressSpinner></REIProgressSpinner>
          </div>
        ))}
      {!loading && !isLoading && (
        <>
          <PortfolioTrackerHeader
            tabIndex={MyTrackerTabs.MyTracker}
            tabName="My Tracker"
            hasProperties={dataExist}
            hasPropertiesWithDifferentCurrency={
              trackerData &&
              trackerData.find((property) => property.HomeCurrency != null && property.HomeCountry != null)
                ? true
                : false
            }
            children={<Filters tab="MyTracker"></Filters>}
          ></PortfolioTrackerHeader>
          <div className="flex flex-row gap-3 content-center">
            <div className="w-1/2">
              {!dataExist && !isLoading && (
                <REIToggablePanel
                  contentComponent={
                    <div className="flex flex-col">
                      <div className="mb-4">
                        Start adding your investment properties to Portfolio Tracker and start measuring your cash flow
                        forecasts against its actuals.
                      </div>
                    </div>
                  }
                  header="Cash Flow"
                  istoggleable={false}
                  defaultCollapse={false}
                ></REIToggablePanel>
              )}
              {dataExist && (
                <CashFlow
                  trackerData={filteredCashFlowData}
                  entityId={selectedEntityId ? selectedEntityId.toString() : ""}
                  showingTotalSummary={isShowingTotals}
                  showPostExpenseData={true}
                  onYearChangeCallback={(year: number) => setSelectedYear(year)}
                  year={Number("20" + selectedYear.toString())}
                ></CashFlow>
              )}
            </div>
            <div className="w-1/2">
              {!dataExist && !isLoading && (
                <REIToggablePanel
                  contentComponent={
                    <div className="flex flex-col">
                      <div className=" mb-4">
                        Start adding your investment properties to Portfolio Tracker and start measuring how your equity
                        looks, what you thought would happen vs what actually has happened.
                      </div>
                    </div>
                  }
                  header="Cash Flow"
                  istoggleable={false}
                  defaultCollapse={false}
                ></REIToggablePanel>
              )}
              {dataExist && !isLoading && (
                <Equity
                  entityId={selectedEntityId ? selectedEntityId.toString() : ""}
                  showingTotalSummary={isShowingTotals}
                  year={Number("20" + selectedYear.toString())}
                  onYearChangeCallback={(year) => setSelectedYear(year)}
                ></Equity>
              )}
            </div>
          </div>
          <div className="flex flex-row content-center">
            <div className="w-full mt-2">
              <KeyMemberAtGlance entityId={selectedEntityId.toString()}></KeyMemberAtGlance>
            </div>
          </div>
          <div className="flex flex-row content-center">
            <div className="w-full mt-2">
              <MyPropertyAtGlance entityId={selectedEntityId.toString()}></MyPropertyAtGlance>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MyTrackerIndex;
